import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function BillsList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [bills, setBills] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedBills, setSelectedBills] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredBills, setFilteredBills] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const fetchBills = async (companyId) => {
        const params = companyId ? { companyId } : {};
        const res = await api.get('/bills', { params });
        const sortedBills = res.data.sort((a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate));
        setBills(sortedBills);
        setFilteredBills(sortedBills);
        setSelectedBills([]);
    };

    useEffect(() => {
        withLoading(async () => {
            const companiesRes = await api.get('/companies');
            const sortedCompanies = [...companiesRes.data].sort((a, b) => a.id - b.id);
            setCompanies(sortedCompanies);

            if (sortedCompanies.length > 0) {
                const defaultCompanyId = String(sortedCompanies[0].id);
                setSelectedCompanyId(defaultCompanyId);
                await fetchBills(defaultCompanyId);
            } else {
                setSelectedCompanyId('');
                await fetchBills(null);
            }
        });
    }, [withLoading]);

    const handleCompanyChange = (e) => {
        const companyId = e.target.value;
        setSelectedCompanyId(companyId);
        withLoading(() => fetchBills(companyId || null));
    };

    useEffect(() => {
        const results = bills.filter(bill =>
            bill.partyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            bill.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredBills(results);
    }, [searchTerm, bills]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });

        const sortedBills = [...filteredBills].sort((a, b) => {
            if (key === 'invoiceDate') {
                return direction === 'asc'
                    ? new Date(a[key]) - new Date(b[key])
                    : new Date(b[key]) - new Date(a[key]);
            }
            if (['totalAmount', 'gstRate', 'gstAmount', 'amountIncGst', 'amountReceived'].includes(key)) {
                return direction === 'asc'
                    ? parseFloat(a[key] || 0) - parseFloat(b[key] || 0)
                    : parseFloat(b[key] || 0) - parseFloat(a[key] || 0);
            }
            return direction === 'asc'
                ? a[key].toString().localeCompare(b[key].toString())
                : b[key].toString().localeCompare(a[key].toString());
        });
        setFilteredBills(sortedBills);
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) return '⇅';
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const handleCheckboxChange = (billId) => {
        setSelectedBills(prev =>
            prev.includes(billId) ? prev.filter(id => id !== billId) : [...prev, billId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedBills(e.target.checked ? filteredBills.map(bill => bill.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected bills?')) return;
        Promise.all(selectedBills.map(billId => api.delete(`/bills/${billId}`)))
            .then(() => {
                setBills(prev => prev.filter(bill => !selectedBills.includes(bill.id)));
                setSelectedBills([]);
            })
            .catch(() => alert('Error deleting Bill'));
    };

    const formatCurrency = (value) =>
        Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const allSelected = filteredBills.length > 0 && selectedBills.length === filteredBills.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Invoices</h1>
                    <p className="list-page-subtitle">
                        {filteredBills.length} invoice{filteredBills.length === 1 ? '' : 's'}
                        {searchTerm ? ' matching your search' : ''}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search invoice or party"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                        {companies.length > 0 && (
                            <>
                                <label htmlFor="bill-company-filter" className="list-filter-label">
                                    Company:
                                </label>
                                <select
                                    id="bill-company-filter"
                                    value={selectedCompanyId}
                                    onChange={handleCompanyChange}
                                    className="list-filter-select"
                                >
                                    <option value="">All</option>
                                    {companies.map((company) => (
                                        <option key={company.id} value={String(company.id)}>
                                            {company.name}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                    </div>
                    <div className="list-button-group">
                        <button type="button" onClick={() => navigate('/createBill')} className="list-add-button">
                            Add New Invoice
                        </button>
                        {selectedBills.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedBills.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th onClick={() => handleSort('invoiceNo')} className="sortable-header">
                                    Invoice No <span className="sort-icon">{getSortIcon('invoiceNo')}</span>
                                </th>
                                <th onClick={() => handleSort('invoiceDate')} className="sortable-header">
                                    Date <span className="sort-icon">{getSortIcon('invoiceDate')}</span>
                                </th>
                                <th onClick={() => handleSort('partyName')} className="sortable-header">
                                    Party Name <span className="sort-icon">{getSortIcon('partyName')}</span>
                                </th>
                                <th onClick={() => handleSort('totalAmount')} className="sortable-header col-num">
                                    Amount <span className="sort-icon">{getSortIcon('totalAmount')}</span>
                                </th>
                                <th onClick={() => handleSort('gstRate')} className="sortable-header col-num">
                                    GST % <span className="sort-icon">{getSortIcon('gstRate')}</span>
                                </th>
                                <th onClick={() => handleSort('gstAmount')} className="sortable-header col-num">
                                    GST amount <span className="sort-icon">{getSortIcon('gstAmount')}</span>
                                </th>
                                <th onClick={() => handleSort('amountIncGst')} className="sortable-header col-num">
                                    Total (Inc. GST) <span className="sort-icon">{getSortIcon('amountIncGst')}</span>
                                </th>
                                <th onClick={() => handleSort('amountReceived')} className="sortable-header col-num">
                                    Amount received <span className="sort-icon">{getSortIcon('amountReceived')}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBills.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="9">
                                        <div className="list-empty-state">
                                            <h3>No invoices found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Create your first invoice to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredBills.map(bill => (
                                <tr
                                    key={bill.id}
                                    className={`list-row${selectedBills.includes(bill.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createBill/${bill.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedBills.includes(bill.id)}
                                            onChange={() => handleCheckboxChange(bill.id)}
                                        />
                                    </td>
                                    <td><span className="list-primary-text">{bill.invoiceNo}</span></td>
                                    <td>{formatDateDDMMYYYY(bill.invoiceDate)}</td>
                                    <td>{bill.partyName}</td>
                                    <td className="col-num">{formatCurrency(bill.totalAmount)}</td>
                                    <td className="col-num">{(Number(bill.gstRate || 0)).toFixed(2)}</td>
                                    <td className="col-num">{formatCurrency(bill.gstAmount)}</td>
                                    <td className="col-num list-primary-text">{formatCurrency(bill.amountIncGst)}</td>
                                    <td className="col-num">{formatCurrency(bill.amountReceived)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default BillsList;
